import { type ReactNode, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Sheet } from '@ai-tutor/ui';
import { ArrowLeft, Sidebar, SlidersHorizontal } from 'lucide-react';
import { useMediaQuery } from '@/hooks/ui/useMediaQuery';

interface MindspaceLayoutProps {
  /** Optional left-panel content. When omitted, the layout collapses to a
   *  single column. Knowledge Archive passes a filter panel; Practice Arena
   *  passes a history sidebar - labels + icon are configurable below. */
  filterPanel?: ReactNode;
  /** Header label shown above the side panel when expanded. */
  panelLabel?: string;
  /** Header icon for the mobile slide-over trigger button. */
  panelIcon?: ReactNode;
  /** Controlled-collapse override. When omitted, the layout owns the state
   *  (uncontrolled mode for Knowledge Archive). Practice Arena uses this to
   *  auto-collapse the sidebar during an active session phase. */
  isPanelCollapsed?: boolean;
  onPanelCollapsedChange?: (next: boolean) => void;
  /** Optional handler for the layout's "back to Mindspace" buttons. When
   *  provided, replaces the default `navigate('/mindspace')` so callers
   *  can flush in-progress state (e.g. partial practice session) before
   *  leaving. */
  onBack?: () => void;
  /**
   * What the panel picks, for a panel that navigates. When it changes, the
   * phone's panel sheet closes, so picking a past session shows that session
   * instead of leaving the sheet over it. (Filters leave it out: they apply
   * live, and the sheet stays while you set several.)
   */
  panelSelection?: string;
  /**
   * The phone's panel sheet, controlled by the page - for a page that puts
   * a labelled trigger in its own header (Practice Arena's "History"). With
   * these the layout draws no panel icon by Back.
   */
  panelOpen?: boolean;
  onPanelOpenChange?: (open: boolean) => void;
  /**
   * Phone only: no Back row and no panel sheet - the page is in a focus mode
   * with its own way out (a practice session's End).
   */
  bare?: boolean;
  children: ReactNode;
}

export function MindspaceLayout({
  filterPanel,
  panelLabel = 'Filters',
  panelIcon,
  isPanelCollapsed,
  onPanelCollapsedChange,
  onBack,
  panelSelection,
  panelOpen,
  onPanelOpenChange,
  bare = false,
  children,
}: MindspaceLayoutProps) {
  const hasFilterPanel = !!filterPanel;
  const navigate = useNavigate();
  const handleBack = () => {
    if (onBack) onBack();
    else navigate('/mindspace');
  };

  // Uncontrolled fallback - kept so Knowledge Archive's existing call site
  // works without changes. When `isPanelCollapsed` is passed, the parent
  // owns the state and the internal value is ignored.
  const [internalCollapsed, setInternalCollapsed] = useState(false);
  const collapsed =
    isPanelCollapsed !== undefined ? isPanelCollapsed : internalCollapsed;
  const setCollapsed = (next: boolean) => {
    if (onPanelCollapsedChange) onPanelCollapsedChange(next);
    if (isPanelCollapsed === undefined) setInternalCollapsed(next);
  };

  // One tree per width, mounted once. Both used to be rendered, one always
  // `display: none`, so every page under this layout mounted twice - its
  // effects, its tracking and its portals included - and stories needed an
  // "exactly one VISIBLE match" helper to find anything.
  const compact = useMediaQuery('(max-width: 767.98px)');
  if (compact) {
    return (
      <PhoneMindspaceLayout
        filterPanel={filterPanel}
        panelLabel={panelLabel}
        panelIcon={panelIcon}
        onBack={handleBack}
        panelSelection={panelSelection}
        panelOpen={panelOpen}
        onPanelOpenChange={onPanelOpenChange}
        bare={bare}
      >
        {children}
      </PhoneMindspaceLayout>
    );
  }

  return (
    <div className='flex h-full flex-col overflow-hidden'>
      {/* 2-panel layout */}
      <div className='flex min-h-0 flex-1 gap-3 overflow-hidden px-6 py-4'>
        {/* Left Panel */}
        {hasFilterPanel && (
          <div
            className='animate-slide-in-left flex-shrink-0 overflow-hidden rounded-lg border border-border/60 bg-card/70 backdrop-blur-sm transition-all duration-300 ease-in-out dark:bg-card'
            style={{
              // Matches the lesson layout's source panel width (350px)
              // so all left-side panels across the app land on the same
              // grid column.
              width: collapsed ? 60 : 350,
              minWidth: collapsed ? 60 : 350,
            }}
          >
            <div className='flex h-full flex-col'>
              {/* Back icon + panel label + collapse toggle.
                  Expanded mode locks to h-16 (64px) so the bottom
                  border aligns with the work-area headers.
                  Collapsed mode keeps its natural height so the
                  stacked icon-button pair doesn't get cropped. */}
              <div
                className={`border-b border-border ${
                  collapsed
                    ? 'flex flex-col items-center gap-2 py-3'
                    : 'flex h-16 items-center justify-between px-4'
                }`}
              >
                <div className='flex items-center gap-2'>
                  <Button
                    variant='ghost'
                    size='sm'
                    onClick={handleBack}
                    aria-label='Back to Mindspace'
                    className='h-9 w-9 p-0 text-muted-foreground hover:bg-accent hover:text-foreground'
                  >
                    <ArrowLeft className='h-4 w-4' />
                  </Button>
                  {!collapsed && (
                    <h3 className='text-base font-medium text-foreground'>
                      {panelLabel}
                    </h3>
                  )}
                </div>
                <Button
                  variant='ghost'
                  size='sm'
                  onClick={() => setCollapsed(!collapsed)}
                  aria-label={
                    collapsed
                      ? `Expand ${panelLabel.toLowerCase()}`
                      : `Collapse ${panelLabel.toLowerCase()}`
                  }
                  className='h-9 w-9 p-0 text-muted-foreground hover:bg-accent hover:text-foreground'
                >
                  <Sidebar className='h-4 w-4' />
                </Button>
              </div>

              <div className='min-h-0 flex-1 overflow-y-auto'>
                {!collapsed && filterPanel}
              </div>
            </div>
          </div>
        )}

        {/* Right Panel - Content */}
        <div className='animate-fade-in-up flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden rounded-lg border border-border/60 bg-card/70 backdrop-blur-sm dark:bg-card'>
          {children}
        </div>
      </div>
    </div>
  );
}

/**
 * The phone panel sheet's open state: the page's when it passes `open`
 * (a trigger in its own header), the layout's otherwise. Either way it closes
 * when what the panel picked changes (`panelSelection`).
 */
function usePanelSheet(
  open: boolean | undefined,
  onOpenChange: ((open: boolean) => void) | undefined,
  selection: string | undefined
): [boolean, (open: boolean) => void, boolean] {
  const [internal, setInternal] = useState(false);
  const controlled = open !== undefined;
  const set = (next: boolean) => {
    onOpenChange?.(next);
    if (!controlled) setInternal(next);
  };
  useEffect(() => {
    setInternal(false);
    onOpenChange?.(false);
    // Keyed on what the panel picked, not on the (per-render) handler.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selection]);
  return [controlled ? open : internal, set, controlled];
}

/**
 * The phone layout: Back (and the panel's trigger), the page, its sheet - a
 * bottom sheet, as the app's other phone panels are, and a real modal dialog,
 * which the hand-rolled drawer it replaced was not.
 */
function PhoneMindspaceLayout({
  filterPanel,
  panelLabel,
  panelIcon,
  onBack,
  panelSelection,
  panelOpen,
  onPanelOpenChange,
  bare,
  children,
}: Pick<
  MindspaceLayoutProps,
  | 'filterPanel'
  | 'panelIcon'
  | 'panelSelection'
  | 'panelOpen'
  | 'onPanelOpenChange'
  | 'children'
> & { panelLabel: string; onBack: () => void; bare: boolean }) {
  const hasFilterPanel = !!filterPanel;
  const [isMobileFilterOpen, setIsMobileFilterOpen, sheetControlled] =
    usePanelSheet(panelOpen, onPanelOpenChange, panelSelection);

  const mobileIcon = panelIcon ?? <SlidersHorizontal className='h-5 w-5' />;

  if (bare) {
    return (
      <div className='flex h-full flex-col overflow-hidden'>{children}</div>
    );
  }
  {
    return (
      <div className='flex h-full flex-col overflow-hidden'>
        {/* Back and the page's panel button. The page's own header (title,
            search, mode) sits right under it and stays put while the page's
            content scrolls, so the way out and the title never scroll away. */}
        <div className='flex flex-shrink-0 items-center justify-between px-2 pt-2'>
          <Button
            variant='ghost'
            onClick={onBack}
            className='h-11 px-2 text-muted-foreground hover:text-foreground'
          >
            <ArrowLeft className='mr-1.5 h-4 w-4' aria-hidden='true' />
            Back<span className='sr-only'> to Mindspace</span>
          </Button>
          {hasFilterPanel && !sheetControlled && (
            <Button
              variant='ghost'
              onClick={() => setIsMobileFilterOpen(true)}
              aria-label={`Open ${panelLabel.toLowerCase()}`}
              aria-haspopup='dialog'
              aria-expanded={isMobileFilterOpen}
              className='h-11 w-11 p-0 text-muted-foreground hover:text-foreground'
            >
              {mobileIcon}
            </Button>
          )}
        </div>

        {/* On the page itself, not in a bordered card inside it: a box with
            its own scroll inside a page read as a page in a page, and its
            border and inset cost 40px of a 390px screen. */}
        <div className='flex min-h-0 flex-1 flex-col overflow-hidden'>
          {children}
        </div>

        {hasFilterPanel && (
          <Sheet
            isOpen={isMobileFilterOpen}
            onClose={() => setIsMobileFilterOpen(false)}
            title={panelLabel}
            size='tall'
          >
            <div className='px-4 pb-[max(1rem,env(safe-area-inset-bottom))]'>
              {filterPanel}
            </div>
          </Sheet>
        )}
      </div>
    );
  }
}
